/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable max-params */
import {
  CellClassParams,
  CellClickedEvent,
  EditableCallbackParams,
  GetQuickFilterTextParams,
  ICellEditorParams,
  ICellRendererParams,
  IRowNode,
  ITooltipParams,
  SuppressKeyboardEventParams,
  ValueFormatterParams,
  ValueGetterParams,
} from '@ag-grid-community/core';
import {
  ColDef,
  ColGroupDef,
  NewValueParams,
  ValueSetterParams,
} from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import { Signal } from '@angular/core';
import dayjs from 'dayjs';
import { isString } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { AgCheckboxRendererComponent } from '@shared/ag-components/ag-checkbox-rerender/ag-checkbox-renderer.component';
import {
  AgCustomHeader,
  AgCustomHeaderParams,
} from '@shared/ag-components/ag-custom-header/ag-custom-header';
import { AgDatePickerComponent } from '@shared/ag-components/ag-date-picker/ag-date-picker.component';
import { AgHtmlHeaderComponent } from '@shared/ag-components/ag-html-header/ag-html-header.component';
import { AgIconLinkCellComponent } from '@shared/ag-components/ag-icon-link-cell/ag-icon-link-cell.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { InvoiceStatus, TrialImplementationStatus } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { InvoiceService } from '@shared/store/invoice/invoice.service';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Option } from '@shared/types/components.type';
import {
  agCostValueSetter,
  agFormatToFullDateValueSetter,
  agFormatToMonthDateValueSetter,
  agInvoiceStatusValueSetter,
  agPaymentStatusValueSetter,
} from '@shared/utils/ag-value-setter.utils';
import { ArrElement, Utils } from '@shared/utils/utils';

import { AgHeaderActionsComponent } from './ag-invoice-actions/ag-header-actions.component';
import {
  AgInvoiceActionsComponent,
  AgInvoiceActionsComponentParamsInput,
} from './ag-invoice-actions/ag-invoice-actions.component';
import { InvoicesGridFormatterService } from './invoices-grid-formatter.service';
import { InvoicesStatusComponent } from './invoices-status.component';
import { IInvoicesGridData, InvoicesComponent } from './invoices.component';
import { InvoiceCheckboxTooltip } from './invoices.constants';
import { ErrorRow } from './invoices.model';
import { PaymentStatusComponent } from './payment-status.component';
import { AgPrepaidHeaderComponent } from './prepaid-header/prepaid-header.component';
import { PurchaseOrdersLink } from './purchase-orders-link';
import { UploadDocumentsDialogComponent } from './upload-documents-dialog/upload-documents-dialog.component';

export const InvoicesColumnDefs = (
  invoicesComponent: InvoicesComponent
): ColDef[] | ColGroupDef[] => {
  const currentOpenMonth = invoicesComponent.mainQuery.getValue().currentOpenMonth;
  const status = invoicesComponent.mainQuery.getSelectedTrial()?.implementation_status;

  return [
    {
      cellClass: ['relative', '!py-[8px]', '!px-[10px]'],
      cellRendererSelector: (params) =>
        params.node.selectable
          ? undefined
          : {
              component: AgHtmlHeaderComponent,
              params: {
                template: `<div class="absolute left-[10px] top-[8px] opacity-15"><span class="ag-icon ag-icon-checkbox-unchecked !text-[var(--ag-input-disabled-border-color)]"></span></div>`,
                tooltip:
                  (status === TrialImplementationStatus.IMPLEMENTATION_STATUS_ARCHIVED ||
                    status === TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE) &&
                  params.node.data.accrual_period &&
                  dayjs(`${params.node.data.accrual_period}-01`).isBefore(currentOpenMonth)
                    ? 'Invoices in closed months cannot be selected for Bulk Edit or Delete functionality.'
                    : 'Invoices with Prepaid Impact cannot be selected for Bulk Edit or Delete functionality.',
              },
            },
      checkboxSelection: true,
      editable: false,
      field: 'checkbox',
      headerCheckboxSelection: true,
      hide: true,
      maxWidth: 35,
      minWidth: 35,
      pinned: invoicesComponent.isJEConfigSetToAttribute() ? 'left' : false,
      resizable: false,
      sortable: false,
      suppressFillHandle: true,
      width: 35,
    },
    {
      cellClass: ['group-cell-icon-block'],
      cellClassRules: {
        'fake-collapse-icon': (params) => {
          return !params.data.invoice_line_item_count && params.node.rowPinned !== 'bottom';
        },
        'opacity-15': (params) => !params.data.invoice_line_item_count,
      },
      cellRenderer: 'agGroupCellRenderer',
      cellStyle: { border: 'none !important' },
      editable: false,
      field: '',
      headerClass: ['border-r-0'],
      headerName: '',
      hide: !invoicesComponent.isJEConfigSetToAttribute(),
      minWidth: 36,
      pinned: 'left',
      resizable: false,
      sortable: false,
      suppressFillHandle: true,
      tooltipValueGetter: (params) => {
        if (!params.data.invoice_line_item_count && params.node?.rowPinned !== 'bottom') {
          return 'No line items to show.';
        }
      },
    },
    ...getInvoiceColumn(
      invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.showInvoiceDetailLink(),
      invoicesComponent.getEditableClass,
      invoicesComponent.hasInvoiceCategoryOutsideBudget,
      invoicesComponent.doesInvoiceMappingsMatchTotal,
      invoicesComponent.editMode,
      invoicesComponent.invoiceService,
      true,
      invoicesComponent.globalStore.primaryAttribute.attribute_name(),
      invoicesComponent.globalStore.secondaryAttribute.attribute_name()
    ),
    ...getTotalAmountColumns(
      invoicesComponent.isJEConfigSetToAttribute(),
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.getEditableClass,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.checkError,
      invoicesComponent.overlayService
    ),
    ...getVendorColumn(
      invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.getEditableClass,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.invoiceService,
      invoicesComponent.organizationQuery
    ),
    ...getFilesColumn(
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.invoiceService,
      invoicesComponent.overlayService,
      invoicesComponent.isInvoiceFinalized,
      invoicesComponent.iCloseMonthsProcessing,
      invoicesComponent.invoiceCtx.invoiceLockTooltip
    ),
    {
      cellClass: invoicesComponent.getEditableClass(['!flex', '!items-center', '!justify-center']),
      cellRenderer: AgCheckboxRendererComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        dontSelectRow: true,
        getDisabledState: () =>
          !invoicesComponent.editMode() ||
          !invoicesComponent.isRowEditable(params as EditableCallbackParams),
        isHidden: params.node.rowPinned === 'bottom',
        tooltip: invoicesComponent.editMode()
          ? ''
          : invoicesComponent.isRowEditable(params as EditableCallbackParams)
            ? InvoiceCheckboxTooltip
            : '',
      }),
      colId: 'is_deposit',
      editable: false,
      field: 'is_deposit',
      headerName: 'Deposit',
      hide: !invoicesComponent.invoicesDepositsEnabled(),
      minWidth: 100,
      suppressFillHandle: true,
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter(''),
      width: 100,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['!flex', '!items-center', '!justify-center']),
      cellRenderer: AgCheckboxRendererComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        dontSelectRow: true,
        getDisabledState: () =>
          !invoicesComponent.editMode() ||
          params.data.hasPrepaidInitialValue ||
          !params.data.organization.id ||
          !invoicesComponent.isRowEditable(params as EditableCallbackParams) ||
          !invoicesComponent.userHasUpdatePrepaidPermission(),
        isHidden: params.node.rowPinned === 'bottom',
        tooltip: invoicesComponent.tooltipValueGetterForPrepaidColumn(params),
      }),
      colId: 'has_prepaid',
      editable: false,
      field: 'has_prepaid',
      headerComponent: AgPrepaidHeaderComponent,
      hide: !invoicesComponent.prepaidEnabled(),
      minWidth: 100,
      onCellValueChanged: (params) => {
        if (params.newValue) {
          invoicesComponent.newPrepaidImpactRows.push({
            id: params.data.id,
            index: params.data.initialRowIndex,
            vendorId: params.data.vendor_id,
          });
        } else {
          invoicesComponent.newPrepaidImpactRows.splice(
            invoicesComponent.newPrepaidImpactRows.findIndex(
              (item) => item.index === params.data.initialRowIndex
            ),
            1
          );
        }

        if (params.node) {
          params.api.refreshCells({
            force: true,
            rowNodes: [params.node],
          });
        }
      },
      suppressFillHandle: true,
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter(''),
      width: 100,
    },
    ...getInvoiceColumn(
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.showInvoiceDetailLink(),
      invoicesComponent.getEditableClass,
      invoicesComponent.hasInvoiceCategoryOutsideBudget,
      invoicesComponent.doesInvoiceMappingsMatchTotal,
      invoicesComponent.editMode,
      invoicesComponent.invoiceService,
      false,
      invoicesComponent.globalStore.primaryAttribute.attribute_name(),
      invoicesComponent.globalStore.secondaryAttribute.attribute_name()
    ),
    ...getVendorColumn(
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.getEditableClass,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.invoiceService,
      invoicesComponent.organizationQuery
    ),
    {
      cellClass: invoicesComponent.getEditableClass(['text-right', 'remove-padding-in-edit-mode']),
      cellClassRules: {
        'border-aux-error': (params) => invoicesComponent.checkError(params, 'accrual_period'),
      },
      cellEditor: AgDatePickerComponent,
      cellEditorParams: (params: ICellEditorParams) => ({
        ignoreValidations: true,
        type: 'month',
        value: params.value || invoicesComponent.invoiceService.trialMonthClose,
      }),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'accrual_period',
      field: 'accrual_period',
      getQuickFilterText(params: GetQuickFilterTextParams) {
        return params.value ? dayjs(params.value).format('MMMM YYYY') : '';
      },
      headerName: 'Posting Period',
      headerTooltip: 'Posting Period',
      minWidth: 195,
      onCellValueChanged: (params) => invoicesComponent.onCellValueChanged(params),
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('valueFormatted'),
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value && params.data.invoice_status !== InvoiceStatus.STATUS_DECLINED
          ? dayjs(params.value).format('MMMM YYYY')
          : Utils.zeroHyphen;
      },
      valueSetter: (params: ValueSetterParams) => agFormatToMonthDateValueSetter(params),
      width: 195,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-right', 'remove-padding-in-edit-mode']),
      cellClassRules: {
        'border-aux-error': (params) =>
          invoicesComponent.checkError(params, 'services_period_trial_timeline'),
      },
      cellEditor: AgDatePickerComponent,
      cellEditorParams: (params: ICellEditorParams) => ({
        ignoreValidations: true,
        type: 'month',
        value: params.value || invoicesComponent.invoiceService.trialMonthClose,
      }),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'services_period',
      field: 'services_period',
      getQuickFilterText(params: GetQuickFilterTextParams) {
        return params.value ? dayjs(params.value).format('MMMM YYYY') : '';
      },
      headerName: 'Service Period',
      headerTooltip: 'Service Period',
      minWidth: 195,
      onCellValueChanged: (params) => invoicesComponent.onCellValueChanged(params),
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('valueFormatted'),
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value ? dayjs(params.value).format('MMMM YYYY') : Utils.zeroHyphen;
      },
      valueSetter: (params: ValueSetterParams) => agFormatToMonthDateValueSetter(params),
      width: 195,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-left']),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'vendor_currency',
      editable: false,
      field: 'organization.currency',
      headerName: 'Vendor Contract Currency',
      headerTooltip: 'Vendor Contract Currency',
      hide: true,
      minWidth: 175,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('data.organization.currency'),
      valueFormatter: Utils.dashFormatter,
      valueGetter: (params: ValueGetterParams) => {
        return params.data?.organization?.currency || '';
      },
      width: 175,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-right']),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'fx_rate',
      editable: false,
      field: 'expense_amounts.invoice_total_trial_currency.exchange_rate',
      headerName: 'Exchange Rate',
      headerTooltip: 'Exchange Rate',
      hide: true,
      minWidth: 175,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter(
        'data.expense_amounts.invoice_total_trial_currency.exchange_rate'
      ),
      valueFormatter: Utils.dashFormatter,
      valueGetter: (params: ValueGetterParams) => {
        return params.data?.expense_amounts.invoice_total_trial_currency.exchange_rate || '';
      },
      width: 175,
    },
    ...getTotalAmountColumns(
      !invoicesComponent.isJEConfigSetToAttribute(),
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.getEditableClass,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.checkError,
      invoicesComponent.overlayService
    ),
    ...getCostCategoryColumns(
      !invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.getEditableClass,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.checkError,
      invoicesComponent.isCategoryOutsideBudget,
      invoicesComponent.editMode,
      invoicesComponent.overlayService
    ),
    ...getPaymentDateColumn(
      invoicesComponent.showInvoicesPaymentDate(),
      invoicesComponent.getEditableClass,
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.onCellValueChanged,
      invoicesComponent.checkError
    ),
    ...getPaymentStatusColumn(
      invoicesComponent.showInvoicesPaymentStatus(),
      invoicesComponent.getEditableClass,
      invoicesComponent.tooltipValueGetter,
      invoicesComponent.invoiceService,
      invoicesComponent.invoicesGridFormatterService
    ),
    {
      cellClass: invoicesComponent.getEditableClass(['text-right']),
      cellEditor: AgDatePickerComponent,
      cellEditorParams: {
        ignoreValidations: true,
      },
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'invoice_date',
      field: 'invoice_date',
      headerName: 'Invoice Date',
      headerTooltip: 'Invoice Date',
      minWidth: 170,
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agDateFormatter,
      valueSetter: (params: ValueSetterParams) => agFormatToFullDateValueSetter(params),
      width: 170,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-left']),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: () => {
        return {
          cellRenderer: InvoicesStatusComponent,
          values: invoicesComponent.invoiceService.invoiceStatusOptions,
        };
      },
      cellRenderer: InvoicesStatusComponent,
      colId: 'invoice_status',
      editable: (params) =>
        invoicesComponent.editMode() &&
        !params.node.rowPinned &&
        invoicesComponent.isRowEditable(params) &&
        !invoicesComponent.savingChanges() &&
        (invoicesComponent.userHasApproveInvoicePermission() ||
          invoicesComponent.authQuery.isAuxAdmin()),
      field: 'invoice_status',
      getQuickFilterText: (params: GetQuickFilterTextParams) =>
        invoicesComponent.invoicesGridFormatterService.getFormattedInvoiceStatus(params.value),
      headerName: 'Status',
      headerTooltip: 'Status',
      minWidth: 200,
      suppressFillHandle: !(
        invoicesComponent.userHasApproveInvoicePermission() ||
        invoicesComponent.authQuery.isAuxAdmin()
      ),
      suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
        return ['Backspace', 'Delete'].includes(params.event.key);
      },
      tooltipValueGetter: (params) => {
        return (
          invoicesComponent.tooltipValueGetter('')(params) ||
          invoicesComponent.invoicesGridFormatterService.getFormattedInvoiceStatus(params.value)
        );
      },
      valueSetter: (params: ValueSetterParams) => agInvoiceStatusValueSetter(params),
      width: 200,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-right']),
      cellEditor: AgDatePickerComponent,
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'due_date',
      field: 'due_date',
      headerName: 'Due Date',
      headerTooltip: 'Due Date',
      minWidth: 170,
      suppressSizeToFit: true,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agDateFormatter,
      valueSetter: (params: ValueSetterParams) => agFormatToFullDateValueSetter(params),
      width: 170,
    },
    {
      cellClass: invoicesComponent.getEditableClass([
        'align-right',
        TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
      ]),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: (params: ICellEditorParams) => {
        const options = invoicesComponent.invoiceService.purchaseOrdersQuery
          .getAll()
          .filter((order) => order.organization?.id === params.data.organization.id)
          .map(({ id }) => id);

        return {
          cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
          values: [null, ...options],
        };
      },
      colId: 'purchase_order_id',
      field: 'po_reference',
      getQuickFilterText: (params: GetQuickFilterTextParams) =>
        invoicesComponent.invoicesGridFormatterService.getFormattedPurchaseOrder(params.value),
      headerClass: [
        'text-center',
        '[&_.ag-header-cell-text]:whitespace-normal',
        '[&_.ag-header-cell-text]:line-clamp-2',
      ],
      headerComponent: AgCustomHeader,
      headerComponentParams: <AgCustomHeaderParams>{
        innerComponent: PurchaseOrdersLink,
        innerComponentInputs: {
          class: 'ag-header-cell-text',
          label: 'PO #',
        },
      },
      headerName: 'PO #',
      headerTooltip: 'PO #',
      minWidth: 100,
      tooltipValueGetter: invoicesComponent.tooltipValueGetter('valueFormatted'),
      valueFormatter: (params: ValueFormatterParams) =>
        invoicesComponent.invoicesGridFormatterService.getFormattedPurchaseOrder(params.value),
      width: 100,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-right']),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'create_date',
      editable: false,
      field: 'create_date',
      getQuickFilterText: Utils.agDateFormatter,
      headerName: 'Date Imported',
      headerTooltip: 'Date Imported',
      minWidth: 120,
      sort: 'desc',
      suppressFillHandle: true,
      tooltipValueGetter: (params) => params.valueFormatted,
      valueFormatter: Utils.agDateFormatter,
      width: 120,
    },
    {
      cellClass: invoicesComponent.getEditableClass(['text-left']),
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'created_by',
      editable: false,
      field: 'created_by',
      getQuickFilterText: (params: GetQuickFilterTextParams) =>
        invoicesComponent.invoiceService.userFormatter(params.value),
      headerClass: ['border-r-0', 'text-center'],
      headerName: 'Created by',
      headerTooltip: 'Created by',
      minWidth: 150,
      suppressFillHandle: true,
      tooltipValueGetter: (params) => params.valueFormatted,
      valueFormatter: (val: ValueFormatterParams) =>
        val.node?.rowPinned
          ? Utils.zeroHyphen
          : invoicesComponent.invoicesGridFormatterService.getFormatterCreateAuthor(
              val,
              invoicesComponent.invoiceService.userFormatter
            ),
      width: 150,
    },
    ...getFilesColumn(
      invoicesComponent.isJEConfigSetToAttribute(),
      invoicesComponent.invoiceService,
      invoicesComponent.overlayService,
      invoicesComponent.isInvoiceFinalized,
      invoicesComponent.iCloseMonthsProcessing,
      invoicesComponent.invoiceCtx.invoiceLockTooltip,
      'right'
    ),
  ];
};

const getFilesColumn = (
  isColumnExist: boolean,
  invoiceService: InvoiceService,
  overlayService: OverlayService,
  isInvoiceFinalized: Signal<boolean>,
  iCloseMonthsProcessing: Signal<boolean | null>,
  invoiceLockTooltip: Signal<string>,
  pinned?: 'left' | 'right'
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  return [
    {
      cellClass: [
        TableConstants.STYLE_CLASSES.CELL_JUSTIFY_CENTER,
        TableConstants.STYLE_CLASSES.EDITABLE_CELL,
      ],
      cellRendererSelector: (params: ICellRendererParams) => {
        if (params.node.rowPinned) {
          return { component: Utils.getCellWrapper('ag-cell-value', 'value') };
        }

        return {
          component: AgInvoiceActionsComponent,
          params: <AgInvoiceActionsComponentParamsInput>{
            downloadClickFN: ({ rowNode }: { rowNode: IRowNode }) => {
              void invoiceService.downloadInvoiceItems(rowNode);
            },
            downloadLinesClickFN: ({ rowNode }: { rowNode: IRowNode }) => {
              invoiceService.downloadInvoiceLines(rowNode);
            },
            iCloseMonthsProcessing: iCloseMonthsProcessing,
            invoiceLockTooltip: invoiceLockTooltip,
            isInvoiceFinalized: isInvoiceFinalized,
            uploadClickFN: async (uploadParams: {
              instance: AgInvoiceActionsComponent;
              rowNode: IRowNode;
            }) => {
              const { instance, rowNode } = uploadParams;

              const filesSaved = await firstValueFrom(
                overlayService.openPopup<
                  { invoice: IInvoicesGridData },
                  unknown[],
                  UploadDocumentsDialogComponent
                >({
                  content: UploadDocumentsDialogComponent,
                  data: { invoice: rowNode.data },
                  settings: {
                    header: 'Upload Document for Invoice',
                    primaryButton: {
                      action: (instance) => instance?.onSave(),
                      disabled: (instance) => !instance?.hasChanges,
                    },
                  },
                }).afterClosed$
              );

              instance.refreshFileState(filesSaved.data);
              instance.params.api.refreshCells();
            },
          },
        };
      },
      editable: false,
      field: 'file',
      headerName: 'Files',
      pinned: pinned || null,
      resizable: false,
      sortable: false,
      suppressFillHandle: true,
      suppressSizeToFit: true,
      width: 100,
    },
  ];
};

const getInvoiceColumn = (
  isColumnExist: boolean,
  showInvoiceDetailLink: boolean,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  hasInvoiceCategoryOutsideBudget: (params: ICellRendererParams) => boolean,
  doesInvoiceMappingsMatchTotal: (params: ICellRendererParams) => boolean,
  editMode: Signal<boolean>,
  invoiceService: InvoiceService,
  pinnedLeft = false,
  primaryAttribute: string,
  secondaryAttribute: string
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  let headerName = '';

  if (primaryAttribute) {
    headerName = 'Invoice # > ' + `${primaryAttribute}`;

    if (secondaryAttribute) {
      headerName = headerName + '/' + `${secondaryAttribute}`;
    }
  } else {
    headerName = 'Invoice #';
  }

  return [
    {
      cellClass: (value) => {
        const classes: string[] = ['text-left'];

        if (!value.node.rowPinned) {
          classes.push(...getEditableClass()(value));

          if (showInvoiceDetailLink) {
            const editable = value.column.isCellEditable(value.node);

            if (editMode() ? editable : true) {
              classes.push('cursor-pointer');
            }

            if (value.data.invoice_no !== null) {
              classes.push('aux-link');
            } else {
              classes.push('aux-link-zero-hyphen');
            }
          }
        }

        return classes;
      },
      cellRenderer: AgIconLinkCellComponent,
      cellRendererParams: (params: ICellRendererParams) => {
        return {
          icons: [
            {
              icon: 'AlertTriangle',
              iconClass: 'text-aux-red-dark',
              iconTooltipMessages: [MessagesConstants.INVOICE.CATEGORY_IS_NOT_IN_BUDGET],
              showIcon: hasInvoiceCategoryOutsideBudget(params),
            },
            {
              icon: 'Check',
              iconClass: 'text-aux-green-dark',
              iconTooltipMessages: [MessagesConstants.INVOICE.TOTAL_MAPPED_TO_ACTIVITIES],
              showIcon: doesInvoiceMappingsMatchTotal(params),
            },
          ],
        };
      },
      colId: 'invoice_no',
      field: 'invoice_no',
      headerClass: [primaryAttribute ? 'text-left' : 'text-center', 'whitespace-normal'],
      headerName: headerName,
      headerTooltip: headerName,
      minWidth: 175,
      onCellClicked: (event: CellClickedEvent) => {
        if (!editMode() && showInvoiceDetailLink) {
          void invoiceService.goToInvoiceDetail(event);
        }
      },
      pinned: pinnedLeft ? 'left' : null,
      valueFormatter: Utils.dashFormatter,
      width: 175,
    },
  ];
};

const getVendorColumn = (
  isColumnExist: boolean,
  tooltipValueGetter: (field: string) => (params: ITooltipParams<IInvoicesGridData>) => string,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  onCellValueChanged: (params: NewValueParams) => void,
  invoiceService: InvoiceService,
  organizationQuery: OrganizationQuery
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  return [
    {
      cellClass: getEditableClass(['text-left']),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: () => {
        return {
          cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
          formatValue: (value?: Option | string) => {
            return isString(value) ? value : value?.label || '';
          },
          values: invoiceService.vendorOptions,
        };
      },
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'vendor_id',
      field: 'organization',
      filterValueGetter: (params: ValueGetterParams) => params.data?.organization?.name,
      headerName: 'Vendor Contract',
      headerTooltip: 'Vendor Contract',
      minWidth: 175,
      onCellValueChanged: (params) => {
        if (params.oldValue !== params.newValue) {
          params.data.organization.currency = organizationQuery.getVendor(
            params.data.organization.id
          )[0].currency;
          params.api.refreshCells({ columns: ['organization.currency'] });
          onCellValueChanged(params);
        }
      },
      tooltipValueGetter: tooltipValueGetter('data.organization.name'),
      valueFormatter: Utils.dashFormatter,
      valueGetter: (params: ValueGetterParams) => {
        return params.data?.organization?.name || '';
      },
      valueSetter: invoiceService.vendorValueSetter,
      width: 175,
    },
  ];
};

const getTotalAmountColumns = (
  isColumnExist: boolean,
  resizable: boolean,
  tooltipValueGetter: (field: string) => (params: ITooltipParams<IInvoicesGridData>) => string,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  onCellValueChanged: (params: NewValueParams) => void,
  checkError: (
    params: CellClassParams<IInvoicesGridData>,
    cause: ArrElement<ErrorRow['causes']>
  ) => boolean,
  overlayService: OverlayService
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  const getTotalHeaderComponent = (isCustomHeader: boolean) => {
    if (isCustomHeader) {
      return { headerComponent: AgHeaderActionsComponent };
    }
  };

  return [
    {
      colId: 'total_contract_amount',
      field: 'expense_amounts.invoice_total.value',
      headerName: 'Total Amount',
      headerTooltip: 'Total Amount',
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(
          params,
          params.data.expense_amounts.invoice_total.currency || params.data.organization.currency
        );
      },
      ...getTotalHeaderComponent(resizable),
      cellClass: (params) => {
        const cls = params.data.organization.currency
          ? `budgetCost${params.data.organization.currency}`
          : 'budgetCostNoSymbol';

        return getEditableClass([cls, 'text-right'])(params);
      },
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      minWidth: 150,
      onCellValueChanged: (params) => onCellValueChanged(params),
      resizable: resizable,
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 150,
    },
    {
      cellClass: getEditableClass(['text-right', 'cost']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'total_amount',
      editable: false,
      field: 'expense_amounts.invoice_total_trial_currency.value',
      headerName: 'Total Amount (USD)',
      headerTooltip: 'Total Amount (USD)',
      hide: true,
      minWidth: 150,
      onCellValueChanged: (params) => onCellValueChanged(params),
      resizable: resizable,
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agCurrencyFormatter,
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 150,
    },
  ];
};

const getCostCategoryColumns = (
  isColumnExist: boolean,
  tooltipValueGetter: (field: string) => (params: ITooltipParams<IInvoicesGridData>) => string,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  onCellValueChanged: (params: NewValueParams) => void,
  checkError: (
    params: CellClassParams<IInvoicesGridData>,
    cause: ArrElement<ErrorRow['causes']>
  ) => boolean,
  isCategoryOutsideBudget: (params: CellClassParams<IInvoicesGridData>, field: string) => boolean,
  editMode: Signal<boolean>,
  overlayService: OverlayService
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  return [
    {
      cellClass: (params) => {
        const cls = params.data.organization.currency
          ? `budgetCost${params.data.organization.currency}`
          : 'budgetCostNoSymbol';

        return getEditableClass([cls])(params);
      },
      cellClassRules: {
        'border-aux-error': (params) =>
          checkError(params, 'totals') ||
          (editMode() && isCategoryOutsideBudget(params, 'services_total')),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'service_contract_amount',
      field: 'expense_amounts.services_total.value',
      headerName: 'Services Total',
      headerTooltip: 'Services Total',
      minWidth: 125,
      onCellValueChanged: (params: NewValueParams) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(
          params,
          params.data.expense_amounts.services_total.currency || params.data.organization.currency
        );
      },
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 125,
    },
    {
      cellClass: (params) => {
        const cls = params.data.organization.currency
          ? `budgetCost${params.data.organization.currency}`
          : 'budgetCostNoSymbol';

        return getEditableClass([cls])(params);
      },
      cellClassRules: {
        'border-aux-error': (params) => {
          return (
            checkError(params, 'totals') ||
            (editMode() && isCategoryOutsideBudget(params, 'discount_total'))
          );
        },
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'discount_contract_amount',
      field: 'expense_amounts.discount_total.value',
      headerName: 'Discount Total',
      headerTooltip: 'Discount Total',
      minWidth: 125,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(
          params,
          params.data.expense_amounts.discount_total.currency || params.data.organization.currency
        );
      },
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 125,
    },
    {
      cellClass: (params) => {
        const cls = params.data.organization.currency
          ? `budgetCost${params.data.organization.currency}`
          : 'budgetCostNoSymbol';

        return getEditableClass([cls])(params);
      },
      cellClassRules: {
        'border-aux-error': (params) =>
          checkError(params, 'totals') ||
          (editMode() && isCategoryOutsideBudget(params, 'investigator_total')),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'investigator_contract_amount',
      field: 'expense_amounts.investigator_total.value',
      headerName: 'Investigator Total',
      headerTooltip: 'Investigator Total',
      minWidth: 150,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(
          params,
          params.data.expense_amounts.investigator_total.currency ||
            params.data.organization.currency
        );
      },
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 150,
    },
    {
      cellClass: (params) => {
        const cls = params.data.organization.currency
          ? `budgetCost${params.data.organization.currency}`
          : 'budgetCostNoSymbol';

        return getEditableClass([cls])(params);
      },
      cellClassRules: {
        'border-aux-error': (params) =>
          checkError(params, 'totals') ||
          (editMode() && isCategoryOutsideBudget(params, 'pass_thru_total')),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'passthrough_contract_amount',
      field: 'expense_amounts.pass_thru_total.value',
      headerName: 'Pass-Through Total',
      headerTooltip: 'Pass-Through Total',
      minWidth: 160,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: (params) => {
        return Utils.agCurrencyFormatter(
          params,
          params.data.expense_amounts.pass_thru_total.currency || params.data.organization.currency
        );
      },
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 160,
    },
    {
      cellClass: getEditableClass(['text-right', 'cost']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'service_amount',
      editable: false,
      field: 'expense_amounts.services_total_trial_currency.value',
      headerName: 'Services Total (USD)',
      headerTooltip: 'Services Total',
      hide: true,
      minWidth: 125,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agCurrencyFormatter,
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 125,
    },
    {
      cellClass: getEditableClass(['text-right', 'cost']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'discount_amount',
      editable: false,
      field: 'expense_amounts.discount_total_trial_currency.value',
      headerName: 'Discount Total (USD)',
      headerTooltip: 'Discount Total',
      hide: true,
      minWidth: 125,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agCurrencyFormatter,
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 125,
    },
    {
      cellClass: getEditableClass(['text-right', 'cost']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'investigator_amount',
      editable: false,
      field: 'expense_amounts.investigator_total_trial_currency.value',
      headerName: 'Investigator Total (USD)',
      headerTooltip: 'Investigator Total',
      hide: true,
      minWidth: 150,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agCurrencyFormatter,
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 150,
    },
    {
      cellClass: getEditableClass(['text-right', 'cost']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'totals'),
      },
      cellDataType: 'text',
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'passthrough_amount',
      editable: false,
      field: 'expense_amounts.pass_thru_total_trial_currency.value',
      headerName: 'Pass-Through Total (USD)',
      headerTooltip: 'Pass-Through Total',
      hide: true,
      minWidth: 160,
      onCellValueChanged: (params) => onCellValueChanged(params),
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agCurrencyFormatter,
      valueSetter: (params: ValueSetterParams) => agCostValueSetter(params, overlayService),
      width: 160,
    },
  ];
};

const getPaymentDateColumn = (
  isColumnExist: boolean,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  tooltipValueGetter: (field: string) => (params: ITooltipParams<IInvoicesGridData>) => string,
  onCellValueChanged: (params: NewValueParams) => void,
  checkError: (
    params: CellClassParams<IInvoicesGridData>,
    cause: ArrElement<ErrorRow['causes']>
  ) => boolean
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  return [
    {
      cellClass: getEditableClass(['text-right']),
      cellClassRules: {
        'border-aux-error': (params) => checkError(params, 'payment_date'),
      },
      cellEditor: AgDatePickerComponent,
      cellEditorParams: {
        ignoreValidations: true,
      },
      cellRenderer: Utils.getCellWrapper('ag-cell-value', 'valueFormatted'),
      colId: 'payment_date',
      field: 'payment_date',
      headerName: 'Payment Date',
      headerTooltip: 'Payment Date',
      minWidth: 170,
      onCellValueChanged: onCellValueChanged.bind(this),
      suppressSizeToFit: true,
      tooltipValueGetter: tooltipValueGetter('valueFormatted'),
      valueFormatter: Utils.agDateFormatter,
      valueSetter: (params: ValueSetterParams) => agFormatToFullDateValueSetter(params),
      width: 170,
    },
  ];
};

const getPaymentStatusColumn = (
  isColumnExist: boolean,
  getEditableClass: (classes?: string[]) => (params: CellClassParams) => string[],
  tooltipValueGetter: (field: string) => (params: ITooltipParams<IInvoicesGridData>) => string,
  invoiceService: InvoiceService,
  invoicesGridFormatterService: InvoicesGridFormatterService
): ColDef[] | ColGroupDef[] => {
  if (!isColumnExist) {
    return [];
  }

  return [
    {
      cellClass: getEditableClass(['text-left']),
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: () => {
        return {
          cellRenderer: PaymentStatusComponent,
          values: invoiceService.paymentStatusOptions,
        };
      },
      cellRenderer: PaymentStatusComponent,
      colId: 'payment_status',
      field: 'payment_status',
      getQuickFilterText: (params: GetQuickFilterTextParams) =>
        invoicesGridFormatterService.getFormattedPaymentStatus(params.value),
      headerName: 'Payment Status',
      headerTooltip: 'Payment Status',
      maxWidth: 175,
      minWidth: 175,
      tooltipValueGetter: (params) => {
        return (
          tooltipValueGetter('')(params) ||
          invoicesGridFormatterService.getFormattedPaymentStatus(params.data.payment_status)
        );
      },
      valueSetter: (params: ValueSetterParams) => agPaymentStatusValueSetter(params),
    },
  ];
};
